// ─────────────────────────────────────────────────────────────────────────────
// SIGNING IN WITH ENTRA
//
// Replaces the Supabase version. Same six exports, same session shape, so the
// forty-odd modules that import from here do not change — the compatibility is
// the point, not an accident.
//
// WHAT SUPABASE WAS DOING FOR US. It redirected to Microsoft, took the response,
// and issued its OWN token containing "role": "authenticated", which is what
// PostgREST reads to decide what the caller may do. Entra issues a token with
// the app role in a "roles" array instead, and PostgREST is configured to look
// there (PGRST_JWT_ROLE_CLAIM_KEY=.roles[0]).
//
// So somebody with no app role assigned signs in successfully and can do
// nothing. That is correct — the assignment list in Entra is the access list
// for Core — but it looks like a broken account, so the message below says so.
//
// TOKENS ARE IN SESSION STORAGE, NOT localStorage. A token in localStorage
// survives the tab closing and is still there for whoever opens the browser
// next — which on a shared machine is somebody else. Session storage dies with
// the tab.
//
// Memory would be better still, and was the first choice, but redirect flow
// unloads the page on the way to Microsoft: anything in memory is gone before
// the response comes back. sessionStorage is what actually works here.
// ─────────────────────────────────────────────────────────────────────────────

import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";

const CLIENT_ID = process.env.REACT_APP_ENTRA_CLIENT_ID;
const TENANT_ID = process.env.REACT_APP_ENTRA_TENANT_ID;
const API_SCOPE = process.env.REACT_APP_ENTRA_API_SCOPE;   // api://<client-id>/access_as_user

// A FUNCTION, not a boolean, because that is what the Supabase version exports
// and what the login page calls. Exported as a value, isAuthConfigured() threw
// "is not a function" during render — which React catches by unmounting the
// tree, so the page goes blank with no visible error. A whole screen lost to
// one pair of brackets.
const configured = Boolean(CLIENT_ID && TENANT_ID && API_SCOPE);
export const isAuthConfigured = () => configured;

let msal = null;
let ready = null;
const listeners = new Set();

function client() {
  if (!configured) return null;
  if (!msal) {
    msal = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin,
      },
      cache: {
        // SESSION STORAGE, because redirect flow navigates away. The page is
        // unloaded on the way to Microsoft and rebuilt on the way back, so
        // anything held in memory is gone before the response arrives —
        // in_mem_redirect_unavailable, which MSAL raises rather than failing
        // obscurely later.
        //
        // I chose memoryStorage for the security of it, then chose redirect
        // flow, and the two are incompatible. sessionStorage is the honest
        // trade: it survives the redirect, and it still dies when the tab
        // closes, so a token is not left sitting on a shared machine the way
        // localStorage would leave it.
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    });
    // REDIRECT FLOW, and handleRedirectPromise is REQUIRED for it — it is what
    // reads the response when Microsoft sends the browser back.
    //
    // Two wrong turns got here. First popup plus handleRedirectPromise: the
    // promise waited for a redirect that a popup flow never produces, so the
    // page sat on "Signing you in…" until MSAL gave up with timed_out. Then
    // popup alone: block_nested_popups, because the redirect URI is the
    // application itself, so the popup loaded Core again, which initialised
    // MSAL, which tried to open another popup.
    //
    // Redirect flow has neither problem, is what the registered redirect URI is
    // for, and survives popup blockers — which matter in a firm where browsers
    // are managed.
    ready = msal.initialize().then(async () => {
      const response = await msal.handleRedirectPromise();
      if (response && response.account) msal.setActiveAccount(response.account);
      return response;
    });
  }
  return msal;
}

// The session shape the rest of Core expects, built from an MSAL account and
// token. identityFromSession reads session.user.email and user_metadata, so
// those are what this produces — changing the shape would mean changing every
// caller for no benefit.
function toSession(account, accessToken) {
  if (!account) return null;
  const claims = account.idTokenClaims || {};
  return {
    access_token: accessToken,
    user: {
      id: account.homeAccountId,
      email: (claims.email || claims.preferred_username || account.username || "").toLowerCase(),
      user_metadata: {
        full_name: claims.name || account.name,
        name: claims.name || account.name,
      },
      // What the token says this person may do. Empty means no app role was
      // assigned, which is a real state and not an error.
      roles: claims.roles || [],
    },
  };
}

export async function signInWithMicrosoft() {
  const c = client();
  if (!c) {
    return { error: { message:
      "Sign-in is not configured. The Entra client id, tenant id and API scope " +
      "need to be set in the environment." } };
  }
  await ready;
  try {
    // Navigates away. Nothing after this runs — the browser goes to Microsoft
    // and comes back to the redirect URI, where handleRedirectPromise above
    // picks the response up.
    await c.loginRedirect({
      scopes: ["openid", "profile", "email", API_SCOPE],
      prompt: "select_account",
    });
    return { data: null, error: null };
  } catch (e) {
    return { error: { message: e && e.message ? e.message : "Sign-in failed." } };
  }
}

export async function signOut() {
  const c = client();
  if (!c) return { error: null };
  await ready;
  try {
    const account = c.getActiveAccount();
    await c.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
  } catch (e) {
    // A failed logout should not strand somebody in a half-signed-in state.
  }
  notify();
  return { error: null };
}

export async function getSession() {
  const c = client();
  if (!c) return null;
  // Bounded. Anything on this path that hangs leaves the login screen showing
  // "Signing you in…" for ever, which is worse than showing the sign-in button
  // — at least a button can be pressed.
  try {
    await Promise.race([
      ready,
      new Promise((_, rej) => setTimeout(() => rej(new Error("init timeout")), 8000)),
    ]);
  } catch (e) {
    return null;
  }
  const account = c.getActiveAccount() || c.getAllAccounts()[0];
  if (!account) return null;
  c.setActiveAccount(account);
  try {
    // Silent first: MSAL re-acquires from the session cookie without a popup,
    // which is why an in-memory cache costs the user nothing.
    const r = await c.acquireTokenSilent({ scopes: [API_SCOPE], account });
    return toSession(account, r.accessToken);
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // The session has genuinely expired. Return null and let the app show the
      // sign-in screen, rather than throwing a popup at somebody mid-task.
      return null;
    }
    return null;
  }
}

export function onAuthChange(cb) {
  listeners.add(cb);
  getSession().then((s) => cb(s)).catch(() => cb(null));
  return () => listeners.delete(cb);
}

function notify() {
  getSession()
    .then((s) => listeners.forEach((cb) => { try { cb(s); } catch (e) {} }))
    .catch(() => listeners.forEach((cb) => { try { cb(null); } catch (e) {} }));
}

// Unchanged from the Supabase version: it reads session.user.email and matches
// it against the staff list. Kept identical rather than rewritten, because it
// carries the fallback matching rules somebody worked out for real names and
// there is no reason to disturb them.
export { identityFromSession } from "./affinity_auth_identity";

// Whether this person can actually do anything. Signing in successfully and
// having no app role is a real state — it means nobody assigned them in Entra —
// and it presents as every action being refused, which reads as a broken system
// rather than a missing assignment.
export function hasAccess(session) {
  return Boolean(session && session.user && (session.user.roles || []).includes("authenticated"));
}

export const NO_ACCESS_MESSAGE =
  "You are signed in, but your account has not been given access to Core. " +
  "Ask IT to assign you in Entra under Enterprise applications → Affinity Core.";
